import React, { useEffect, useMemo } from 'react';
import { BrowserRouter as Router, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Provider } from 'react-redux';
import { useAppDispatch, useAppSelector } from './redux/hook';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from './redux/store';
import { theme } from './styles/theme';
import { catalogDarkTheme } from './styles/theme/catalogDarkTheme';
import { ColorModeProvider, useColorMode } from './context/ColorModeContext';
import AppRoutes from './AppRoutes';
import { ProductMetadataProvider } from './hooks';
import ErrorBoundary from './components/common/ErrorBoundary';
import { setNavigateFunction } from './utils/authErrorHandler';
import { fetchProfile, selectAuthToken } from './redux/slices/auth.slice';
import { installNumberInputWheelGuard } from './utils/numberInputWheelGuard';

// Component to set up navigation function for error handler and fetch profile on mount
const NavigationSetup: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const token = useAppSelector(selectAuthToken);

  useEffect(() => {
    // Set the navigate function so error handler can use React Router navigation
    setNavigateFunction((path: string) => {
      navigate(path);
    });
  }, [navigate]);

  // Fetch profile on mount if we have a token (even if isAuthenticated is false during rehydration)
  // This ensures permissions are loaded on page refresh
  useEffect(() => {
    if (token != null && token !== '') {
      void dispatch(fetchProfile())
        .unwrap()
        .catch((error: unknown) => {
          console.error('App: Failed to fetch profile on initialization:', error);
        });
    }
  }, [token, dispatch]);

  return null;
};

const ThemedApplication: React.FC = () => {
  const { mode } = useColorMode();
  const muiTheme = useMemo(() => (mode === 'dark' ? catalogDarkTheme : theme), [mode]);

  useEffect(() => installNumberInputWheelGuard(), []);

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline enableColorScheme />
      <ProductMetadataProvider>
        <Router>
          <NavigationSetup />
          <AppRoutes />
        </Router>
      </ProductMetadataProvider>
    </ThemeProvider>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <ColorModeProvider>
            <ThemedApplication />
          </ColorModeProvider>
        </PersistGate>
      </Provider>
    </ErrorBoundary>
  );
};

export default App;
